import LoginInertiaController from '@/actions/App/Http/Controllers/User/Auth/LoginInertiaController';
import ProfileInertiaController from '@/actions/App/Http/Controllers/User/ProfileInertiaController';
import UserInertiaController from '@/actions/App/Http/Controllers/User/UserInertiaController';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { cn } from '@/lib/utils';
import { type SharedData } from '@/types';
import { type NavSite } from '@/types/search';
import { Link, router, usePage } from '@inertiajs/react';
import { ChevronDown, LogOut, ShoppingCart, TicketCheck, User, User2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import AppLogo from '../../app-logo';
import { NavMegaPanel } from './header/nav-mega-panel';
import { NavMobileDrawer } from './header/nav-mobile-drawer';
import { useFloatingNav } from './header/use-floating-nav';
import { useNavItems } from './header/use-nav-items';
import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';

interface WebHeaderProps {
  cartCount?: number;
  /**
   * Set `false` on app surfaces that own their own sticky chrome (e.g. the portal tab
   * rail). The header stays an opaque white bar at every scroll position — no pill morph,
   * no transparent band, no shadow spill onto the rail below.
   *
   * Defaults to `true` so the public site keeps its scroll-morphing behaviour unchanged.
   */
  floating?: boolean;
}

/** Grace period for the pointer to cross the gap between a trigger and its panel. */
const CLOSE_GRACE_MS = 140;

/** One curve for every morphing property — eight curves would read as eight animations. */
const MORPH = 'duration-[520ms] ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none';

/**
 * Public site header — a scroll-morphing bar (N10) with a mega-menu panel (N11).
 *
 * The morph obeys four rules, and breaking any one of them is what makes this pattern
 * read as broken:
 *
 * 1. Total height never changes. The `h-20` wrapper is fixed; only the element *inside*
 *    it morphs, so nothing below the header ever shifts mid-scroll.
 * 2. The floating offset is a `transform`, never padding or margin — transforms don't
 *    touch layout.
 * 3. Every visual the outer owns at rest (background, border, shadow, blur) is explicitly
 *    neutralised in the floating state, or it drags across the viewport behind nothing.
 * 4. One duration and one easing for all of it.
 */
export function WebHeader({ cartCount: propCartCount, floating = true }: WebHeaderProps) {
  const page = usePage<SharedData>();
  const { auth, sites, navSites } = page.props as SharedData & { sites?: NavSite[]; navSites?: NavSite[] };

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  // Lags one step behind `activeIndex` so the panel keeps its content while it fades out.
  const [renderedIndex, setRenderedIndex] = useState<number | null>(null);
  // Gates the panel's thumbnails — see NavMegaPanel's `showMedia`.
  const [hasOpened, setHasOpened] = useState(false);

  const headerRef = useRef<HTMLElement>(null);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scrolledPastThreshold = useFloatingNav();
  // Gate the morph behind the `floating` prop — set `false` on portal pages so the
  // pill never detaches and overlaps the sticky tab rail.
  const isFloating = floating && scrolledPastThreshold;
  const { count: realCartCount } = useUserGlobalCart();
  const navItems = useNavItems(navSites, sites);

  const cartCount = realCartCount || propCartCount || 0;
  // Split on `?` so query strings (utm tags, referrals) don't break the over-hero treatment.
  const isHomePage = page.url.split('?')[0] === '/';
  const isPanelOpen = activeIndex !== null;
  const isTransparent = isHomePage && !isFloating && !isPanelOpen;

  useEffect(() => {
    if (activeIndex !== null) setRenderedIndex(activeIndex);
  }, [activeIndex]);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const closeNow = useCallback(() => {
    cancelClose();
    setActiveIndex(null);
  }, [cancelClose]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => setActiveIndex(null), CLOSE_GRACE_MS);
  }, [cancelClose]);

  const openPanel = useCallback(
    (index: number) => {
      cancelClose();
      setHasOpened(true);
      setActiveIndex(index);
    },
    [cancelClose],
  );

  useEffect(() => cancelClose, [cancelClose]);

  // Esc closes and hands focus back to the trigger that opened the panel.
  useEffect(() => {
    if (activeIndex === null) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const trigger = triggerRefs.current[activeIndex];
      closeNow();
      trigger?.focus();
    };

    const onPointerDown = (event: MouseEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) closeNow();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [activeIndex, closeNow]);

  const handleLogout = () => {
    router.flushAll();
    router.get(LoginInertiaController.logout().url);
  };

  // The panel is mounted from the first render — a panel that mounts *already open* skips
  // its own enter transition, so the first hover would pop instead of animate. Before any
  // interaction it shows the first dropdown's groups, closed.
  const panelIndex = renderedIndex ?? navItems.findIndex((item) => item.groups);
  const renderedGroups = panelIndex >= 0 ? navItems[panelIndex]?.groups : undefined;

  return (
    <header
      ref={headerRef}
      className={cn(
        'sticky top-0 z-[50] w-full',
        'transition-[background-color,border-color,box-shadow,backdrop-filter]',
        MORPH,
        isFloating
          ? // Law 3: hand every visual over to the pill. `blur(0)` rather than `none` —
            // `none` snaps instead of transitioning.
            'border-transparent bg-transparent shadow-none backdrop-blur-[2px]'
          : isTransparent
            ? 'border-transparent bg-gradient-to-b from-black/15 to-transparent text-white shadow-none  backdrop-blur-[0px]'
            : 'border-gray-200/60 bg-white shadow-sm backdrop-blur-[12px]',
      )}
    >
      {/* Scrim sits inside the header's stacking context, so a negative z-index paints it
          above the page but below the nav itself. */}
      <div
        aria-hidden="true"
        onClick={closeNow}
        className={cn(
          'fixed inset-0 -z-10 bg-gray-500/25 backdrop-blur-[4px] transition-[opacity,visibility]',
          MORPH,
          isPanelOpen ? 'visible opacity-100' : 'invisible opacity-0',
        )}
      />

      {/* Law 1: this wrapper's height is fixed in both states. */}
      <div className="mx-auto flex h-20 w-full items-center px-4">
        <div
          className={cn(
            'relative mx-auto flex h-16 w-full items-center justify-between transition-all',
            MORPH,
            isFloating
              ? // Law 2: the detachment is a transform, so layout below stays untouched.
                'h-14 max-w-5xl translate-y-2 rounded-full bg-white/90 px-6 backdrop-blur-[18px] backdrop-saturate-150 motion-reduce:translate-y-0 ' +
                  'shadow-[0_12px_40px_-14px_rgba(15,23,42,0.35),0_2px_10px_-4px_rgba(255,64,64,0.25),inset_0_1px_0_rgba(255,255,255,0.7)]'
              : 'max-w-7xl translate-y-0 rounded-none bg-transparent px-0 shadow-none backdrop-blur-[0px] backdrop-saturate-100',
          )}
        >
          <div className="flex min-w-0 items-center gap-8">
            <Link href="/" className="flex shrink-0 items-center" onMouseEnter={closeNow}>
              <AppLogo />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex" aria-label="Điều hướng chính">
              {navItems.map((item, index) =>
                item.groups ? (
                  <button
                    key={item.title}
                    ref={(node) => {
                      triggerRefs.current[index] = node;
                    }}
                    type="button"
                    aria-expanded={activeIndex === index}
                    aria-controls="web-nav-panel"
                    onMouseEnter={() => openPanel(index)}
                    onMouseLeave={scheduleClose}
                    onClick={() => (activeIndex === index ? closeNow() : openPanel(index))}
                    className={cn(
                      'focus-visible:ring-ring flex cursor-pointer items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 focus-visible:ring-2 focus-visible:outline-none',
                      isTransparent ? 'text-white hover:text-white/80' : 'hover:text-primary text-gray-900',
                    )}
                  >
                    <span
                      className={cn(
                        'border-b-2 border-transparent pb-0.5 transition-colors duration-200',
                        activeIndex === index && (isTransparent ? 'border-white' : 'border-primary'),
                      )}
                    >
                      {item.title}
                    </span>
                    <ChevronDown className={cn('size-4 transition-transform duration-200', activeIndex === index && 'rotate-180')} />
                  </button>
                ) : (
                  <Link
                    key={item.title}
                    href={item.href || '#'}
                    onMouseEnter={scheduleClose}
                    className={cn(
                      'focus-visible:ring-ring rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 focus-visible:ring-2 focus-visible:outline-none',
                      isTransparent ? 'text-white hover:text-white/80' : 'hover:text-primary text-gray-900',
                    )}
                  >
                    {item.title}
                  </Link>
                ),
              )}

              <Button  className="ml-3 h-10 rounded-full px-7" asChild onMouseEnter={scheduleClose}>
                <Link href={SiteInertiaController.search().url}>Đặt vé</Link>
              </Button>
            </nav>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Button variant="default" size="icon" className="relative rounded-full" asChild>
              <Link href="/cart" aria-label={`Giỏ hàng${cartCount > 0 ? ` (${cartCount})` : ''}`}>
                <ShoppingCart className="size-4" />
                {/* An empty cart shouldn't wear a red "0". */}
                {cartCount > 0 && (
                  <span className="bg-primary absolute -top-1.5 -right-1.5 min-w-5 rounded-full border border-white px-1 text-[11px] text-center leading-4 text-white">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </Link>
            </Button>

            {auth?.user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost"  className={cn("flex items-center gap-1 rounded-full bg-gray-100 px-3!",
                    isTransparent && 'border border-white/40 bg-white/10',)}>
                    <span
                      className={cn(
                        'flex items-center justify-center ',
                      )}
                    >
                      <User className="size-4" />
                    </span>
                    <span className={cn('hidden max-w-40 truncate sm:inline', isTransparent && 'text-white')}>
                      {auth.user.firstname} {auth.user.lastname}
                    </span>
                    <ChevronDown className={cn('size-4', isTransparent && 'text-white')} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="z-[52] w-[200px]" align="end">
                  <DropdownMenuItem asChild>
                    <Link href={UserInertiaController.home().url} className="w-full cursor-pointer">
                      <TicketCheck className="size-4" />
                      Vé của tôi
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href={ProfileInertiaController.profile().url} className="w-full cursor-pointer">
                      <User2 className="size-4" />
                      Hồ sơ
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href={LoginInertiaController.logout().url}
                      className="w-full cursor-pointer"
                      onClick={(event) => {
                        event.preventDefault();
                        handleLogout();
                      }}
                    >
                      <LogOut className="size-4" />
                      Đăng xuất
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="outline_primary" className="rounded-full bg-white" asChild>
                <Link href="/user/login" className="flex items-center gap-2 font-medium">
                  <User className="size-4" />
                  <span className="hidden sm:inline">Đăng nhập</span>
                </Link>
              </Button>
            )}

            <div className="flex items-center lg:hidden">
              <NavMobileDrawer
                navItems={navItems}
                cartCount={cartCount}
                isAuthenticated={Boolean(auth?.user)}
                isOpen={isMenuOpen}
                onOpenChange={setIsMenuOpen}
                onLogout={handleLogout}
              />
            </div>
          </div>

          {/* Nested inside the morphing element, so it inherits the pill's width and offset. */}
          {renderedGroups && (
            <NavMegaPanel
              id="web-nav-panel"
              groups={renderedGroups}
              isOpen={isPanelOpen}
              showMedia={hasOpened}
              onNavigate={closeNow}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            />
          )}
        </div>
      </div>
    </header>
  );
}
